// The candidate profile's dialogs (lane B3 S12), each opened from the URL so a capture frame can show it:
// `?action=edit` Edit profile — a changed email retires the old contact and the Candidate ID stays; a stale version
// shows what changed and Reload;
// `?action=link` Link to requisition — the new application under the existing Candidate ID (a requisition with no
// Recruiter is listed and blocked with its assignment status);
// `?action=designate` Designate — his five reasons, segment, tags, prior requisition, last contact and notes, and no
// campaign control ("do not auto-enroll in campaigns"); Segment is a taxonomy the Admin publishes (CR-066): with none
// published the field stays, dashed and described by his sentence (TaxonomySelect, shared with the profile's tabs and
// the Talent community page);
// `?action=merge[&with=<token>]` Merge — the Admin chooses the profile to keep (*Use existing*) and each value in the
// preview; a reason; nothing moves;
// `?action=recover` Recover — the merge in effect, reversed with a reason (the Admin's own act, DC-228; no override
// path). The control of a seat that may not do it is absent (DC-24). His BRD
// sentences are never a standing hint or banner here (UAT F-32, Q-4's default); a blocked control or a refusal still
// names its rule.

import { useMemo, useState, type ReactNode } from "react";
import { AppError, callTool } from "@/api/runtime";
import type {
  CandidateLinkToRequisitionOutput, CandidateMergeOutput, CandidateProfileOutput, CandidateUnmergeInput, ProfileChanges, SearchRow,
} from "@/api/tools";
import {
  candidateCommunityAdd, candidateLinkToRequisition, candidateMerge, candidateProfile, candidateSearch, candidateUpdateProfile,
  configOptions, requisitionList,
} from "@/api/tools";
import { useWrite } from "@/api/useTool";
import { go, useRoute } from "@/router";
import { useSession } from "@/session/useSession";
import {
  ACTION_LABELS, Banner, Button, Chip, Chips, Compare, Dialog, DIALOG_LABELS, Diff, DOCUMENT_LABELS, ErrorStrip, Field, FilterBar,
  G1_LABELS, G6_LABELS, G6_RULE_LABELS, Grid, IdPill, Input, KeyValue, PersonPick, PROFILE_STATUS, RadioTile, RECRUITER_ASSIGNMENT_STATUS,
  RULE_LABELS, Select, Stack, SYNC_LABELS, Table, Td, TextArea, TextLink, Th, Tr, type CompareAttribute, type Hs,
} from "@/mc";
import { BRD } from "./brd";
import { usePeopleChoices, type Choice } from "./options";
import { queryOf, useSettledRead, withQuery } from "@/views/shared/settledRead";
import { CANDIDATE_LABELS as L, PROFILE_LABELS as P, PROFILE_RULES } from "./labels";
import {
  EDITABLE, MERGE_ROWS, mayChangeOwner, mergeKeep, profileChanges, profileHref, REASONS_ADDED, TAG, tagSlug, type Editable,
} from "./profile";
import type { ProfileSubject } from "./ProfilePage";
import { refusalSentence, ruleCode } from "./refusals";

/** A dialog's outcome: his sentence for a refusal (entered values kept), the error strip for a failure. */
function useDialogOutcome() {
  const [refused, setRefused] = useState<{ reason: string; stale: boolean } | null>(null);
  const [failure, setFailure] = useState<AppError | null>(null);
  const run = async <T,>(work: () => Promise<T>): Promise<T | null> => {
    setRefused(null);
    setFailure(null);
    try {
      return await work();
    } catch (error) {
      if (error instanceof AppError && (error.status >= 500 || error.code === "NETWORK")) setFailure(error);
      else setRefused({ reason: refusalSentence(error) ?? RULE_LABELS.failedSyncRetryable, stale: error instanceof AppError && ruleCode(error) === "stale_version" });
      return null;
    }
  };
  const strip = failure ? (
    <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: failure.status, refusal_code: failure.code, request_id: failure.requestId }} />
  ) : null;
  return { refused, strip, run, clear: () => { setRefused(null); setFailure(null); } };
}

export function ProfileDialogs({ profile, subject, flags, onDone }: {
  profile: CandidateProfileOutput;
  subject: ProfileSubject;
  flags: URLSearchParams;
  onDone: () => void;
}) {
  const { can, me } = useSession();
  const action = flags.get("action");
  const here = profileHref(subject);
  const close = () => go(withQuery(here, { action: null, with: null }, flags));
  const full = profile.projection === "full";
  const active = profile.status !== "Merged";
  const roles = me?.roles ?? [];
  if (action === "edit" && can("candidate.update_profile") && full && active) {
    return <EditProfileDialog profile={profile} roles={roles} onClose={close} onDone={() => { onDone(); close(); }} onReload={onDone} />;
  }
  if (action === "link" && can("candidate.link_to_requisition") && active && profile.projection !== "token") {
    return (
      <LinkDialog
        subject={subject}
        roles={roles}
        onClose={close}
        onLinked={(out) => {
          onDone();
          go(out.candidate_id && !subject.token ? profileHref({ token: out.candidate_id }, "applications") : profileHref(subject, "applications"));
        }}
      />
    );
  }
  if (action === "designate" && can("candidate.community_add") && full && active && profile.candidate_id) {
    return <DesignateDialog profile={profile} candidateId={profile.candidate_id} onClose={close} onDone={() => { onDone(); go(profileHref(subject, "talent-community")); }} />;
  }
  if (action === "merge" && can("candidate.merge") && full && active && profile.candidate_id) {
    return <MergeDialog profile={profile} candidateId={profile.candidate_id} withToken={flags.get("with")} flags={flags} onClose={close} />;
  }
  if (action === "recover" && can("candidate.unmerge") && profile.recovery && profile.candidate_id) {
    return <RecoverDialog profile={profile} candidateId={profile.candidate_id} onClose={close} onDone={() => { onDone(); close(); }} />;
  }
  return null;
}

// ---- Edit profile ----------------------------------------------------------------------------------------------------
function recorded(profile: CandidateProfileOutput): Record<Editable, string> {
  return {
    display_name: profile.display_name ?? "",
    primary_email: profile.email ?? "",
    secondary_email: "",
    phone: profile.phone ?? "",
    linkedin_url: profile.linkedin_url ?? "",
    location: profile.location ?? "",
    current_employer: profile.current_employer ?? "",
  };
}

function EditProfileDialog({ profile, roles, onClose, onDone, onReload }: {
  profile: CandidateProfileOutput;
  roles: readonly string[];
  onClose: () => void;
  onDone: () => void;
  onReload: () => void;
}) {
  const before = useMemo(() => recorded(profile), [profile]);
  const [values, setValues] = useState(before);
  const [ownerId, setOwnerId] = useState(profile.owner_id ?? "");
  const [reason, setReason] = useState("");
  const [base, setBase] = useState({ version: profile.version, values: before });
  const outcome = useDialogOutcome();
  const ownerChange = mayChangeOwner(roles);
  const choices = usePeopleChoices("recruiter", ownerChange);
  const update = useWrite(
    (input: Parameters<typeof candidateUpdateProfile>[0], key) => candidateUpdateProfile(input, key),
    [["candidate-profile"], ["candidate-search"], ["candidate-timeline"]],
  );
  const changes = profileChanges(base.values, values, ownerChange ? { before: profile.owner_id ?? "", after: ownerId } : undefined);
  const changed = Object.keys(changes).length > 0;
  const overwrites = EDITABLE.some(({ key }) => key in changes && base.values[key].trim() !== "") || "owner_id" in changes;
  const blocked = !changed ? { reason: PROFILE_RULES.materialUpdates }
    : overwrites && reason.trim().length < 3 ? { reason: PROFILE_RULES.materialUpdates } : undefined;
  const newer: ReactNode = outcome.refused?.stale ? (
    <Stack gap={8}>
      <Diff form="cols" rows={EDITABLE.filter(({ key }) => before[key] !== base.values[key]).map(({ key, label }) => ({ label, old: base.values[key] || "—", new: before[key] || "—", changed: true }))} />
      <Button tone="ghost" sm onClick={() => { onReload(); setBase({ version: profile.version, values: before }); setValues(before); outcome.clear(); }} >
        {ACTION_LABELS.retry}
      </Button>
    </Stack>
  ) : undefined;
  return (
    <Dialog
      title={DIALOG_LABELS.editProfile}
      width={640}
      onClose={onClose}
      dismissLabel={ACTION_LABELS.cancel}
      refused={outcome.refused ? { reason: outcome.refused.stale ? PROFILE_RULES.optimisticConcurrency : outcome.refused.reason, newer } : undefined}
      primary={{
        label: ACTION_LABELS.save,
        blocked,
        busy: update.isPending,
        onClick: () => {
          void outcome.run(() => update.mutateAsync({ candidate_id: profile.candidate_id ?? "", expected_version: base.version, changes, reason: reason.trim() || null }))
            .then((out) => { if (out) onDone(); });
        },
        hs: "cand.edit.save",
      }}
      
    >
      <Stack gap={12}>
        {outcome.strip}
        <Grid cols={2} gap={12}>
          {EDITABLE.map(({ key, label, hint }) => (
            <Field key={key} label={label} hint={hint}>
              <Input value={values[key]} onChange={(event) => setValues((prev) => ({ ...prev, [key]: event.target.value }))} autoComplete="off" hs={`cand.edit.${key.replace(/_/g, "-")}`} />
            </Field>
          ))}
        </Grid>
        {ownerChange ? (
          <Field label={L.owner}>
            <PersonPick
              options={choices.choices.map((choice) => ({ value: choice.value, name: choice.label }))}
              value={ownerId || undefined}
              onChange={setOwnerId}
              loading={choices.isLoading}
              
            />
          </Field>
        ) : null}
        <Field label={G1_LABELS.reason} required={overwrites} hint={PROFILE_RULES.materialUpdates}>
          <TextArea rows={2} value={reason} onChange={(event) => setReason(event.target.value)}  />
        </Field>
      </Stack>
    </Dialog>
  );
}

// ---- A taxonomy field (CR-066) ---------------------------------------------------------------------------------------
/** One taxonomy the Admin publishes, read as `config.options` serves it (the key options.ts reads it by): the active
 *  choices, and whether the read is still on its way. */
export interface Taxonomy {
  choices: readonly Choice[];
  loading: boolean;
}

export function useTaxonomy(name: "segment" | "mobility_type", enabled = true): Taxonomy {
  const read = useSettledRead(["config-options", "taxonomies", name], () => configOptions({ kind: "taxonomies", name }), {
    enabled,
    staleTime: 5 * 60_000,
  });
  const choices = useMemo(() => (read.data?.options ?? []).map((option) => ({ value: option.slug, label: option.label })), [read.data]);
  return { choices, loading: enabled && read.isLoading };
}

/** Segment or Mobility type: his configured values; while the Admin has published none, the field is still drawn —
 *  dashed, aria-disabled and described by "Admin may configure statuses, reason lists, taxonomies, thresholds,
 *  reminders, templates and segments." (DC-24) — never left out (Q-D14 asks him for the lists). */
export function TaxonomySelect({ label, taxonomy, value, onChange, sm, hs }: {
  label: string;
  taxonomy: Taxonomy;
  value: string;
  onChange: (value: string) => void;
  sm?: boolean | undefined;
  hs: Hs;
}) {
  const unpublished = !taxonomy.loading && taxonomy.choices.length === 0;
  return (
    <Field label={label} blocked={unpublished ? { reason: G6_RULE_LABELS.adminConfigures } : undefined}>
      <Select options={taxonomy.choices} value={value} onChange={onChange} loading={taxonomy.loading} sm={sm} hs={hs} />
    </Field>
  );
}

// ---- Link to requisition ---------------------------------------------------------------------------------------------
const LINKABLE = new Set(["open", "partially_filled"]);

export function LinkDialog({ subject, roles, onClose, onLinked }: {
  subject: { token: string | null; ref: string | null };
  roles: readonly string[];
  onClose: () => void;
  onLinked: (out: CandidateLinkToRequisitionOutput) => void;
}) {
  const route = useRoute();
  const flags = queryOf(route.raw);
  const recruiterOnly = !roles.some((role) => ["coordinator", "ta_manager", "admin"].includes(role));
  const list = useSettledRead(["candidate-link-requisitions", recruiterOnly], () => requisitionList({ owner: recruiterOnly ? "mine" : "anyone", page_size: 100 }));
  const [requisitionId, setRequisitionId] = useState(flags.get("req") ?? "");
  const outcome = useDialogOutcome();
  const link = useWrite(
    (input: Parameters<typeof candidateLinkToRequisition>[0], key) => candidateLinkToRequisition(input, key),
    [["candidate-profile"], ["candidate-search"], ["candidate-timeline"]],
  );
  const options = (list.data?.items ?? [])
    .filter((row) => "status" in row && LINKABLE.has(String(row.status)))
    .map((row) => {
      const unassigned = !("owner_id" in row) || !row.owner_id;
      return {
        value: row.id,
        label: unassigned ? `${row.id} · ${row.title} · ${RECRUITER_ASSIGNMENT_STATUS.pendingTaManager}` : `${row.id} · ${row.title}`,
        disabled: unassigned,
      };
    });
  return (
    <Dialog
      preset="linkToRequisition"
      onClose={onClose}
      dismissLabel={ACTION_LABELS.cancel}
      refused={outcome.refused ? { reason: outcome.refused.reason } : undefined}
      primary={{
        label: DIALOG_LABELS.linkToRequisition,
        blocked: requisitionId ? undefined : { reason: BRD.links },
        busy: link.isPending,
        onClick: () => {
          const who = subject.token ? { candidate_id: subject.token } : { match_ref: subject.ref };
          void outcome.run(() => link.mutateAsync({ requisition_id: requisitionId, ...who })).then((out) => { if (out) onLinked(out); });
        },
        hs: "cand.link.save",
      }}
      
    >
      <Stack gap={12}>
        {outcome.strip}
        <Field label={BRD.requisition} required>
          <Select options={options} value={requisitionId} onChange={setRequisitionId} loading={list.isLoading}  />
        </Field>
      </Stack>
    </Dialog>
  );
}

// ---- Designate --------------------------------------------------------------------------------------------------------
function DesignateDialog({ profile, candidateId, onClose, onDone }: {
  profile: CandidateProfileOutput;
  candidateId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const segments = useTaxonomy("segment");
  const [reasonAdded, setReasonAdded] = useState("");
  const [segment, setSegment] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagText, setTagText] = useState("");
  const [prior, setPrior] = useState("");
  const [lastContact, setLastContact] = useState("");
  const [notes, setNotes] = useState("");
  const outcome = useDialogOutcome();
  const add = useWrite(
    (input: Parameters<typeof candidateCommunityAdd>[0], key) => candidateCommunityAdd(input, key),
    [["candidate-profile"], ["candidate-search"], ["candidate-timeline"]],
  );
  const priorOptions = (profile.applications ?? [])
    .filter((app) => app.application_id)
    .map((app) => ({ value: app.application_id ?? "", label: `${app.requisition_id} · ${app.title}` }));
  const addTag = () => {
    const slug = tagSlug(tagText);
    if (TAG.test(slug) && !tags.includes(slug) && tags.length < 10) setTags([...tags, slug]);
    setTagText("");
  };
  return (
    <Dialog
      preset="designate"
      width={600}
      onClose={onClose}
      dismissLabel={ACTION_LABELS.cancel}
      refused={outcome.refused ? { reason: outcome.refused.reason } : undefined}
      primary={{
        label: ACTION_LABELS.designate,
        blocked: reasonAdded ? undefined : { reason: PROFILE_RULES.intentionallyAdds },
        busy: add.isPending,
        onClick: () => {
          void outcome.run(() => add.mutateAsync({
            candidate_id: candidateId,
            reason_added: reasonAdded,
            segment: segment || null,
            tags,
            prior_application_id: prior || null,
            last_contact_at: lastContact ? new Date(`${lastContact}T12:00:00Z`).toISOString() : null,
            notes: notes.trim() || null,
          })).then((out) => { if (out) onDone(); });
        },
        hs: "cand.designate.save",
      }}
      
    >
      <Stack gap={12}>
        {outcome.strip}
        <Field label={G1_LABELS.reason} required hint={PROFILE_RULES.intentionallyAdds}>
          <Select options={REASONS_ADDED} value={reasonAdded} onChange={setReasonAdded}  />
        </Field>
        <TaxonomySelect label={P.segment} taxonomy={segments} value={segment} onChange={setSegment}  />
        <Field label={P.tags}>
          <Stack gap={6}>
            <Input
              value={tagText}
              onChange={(event) => setTagText(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addTag(); } }}
              onBlur={() => { if (tagText.trim()) addTag(); }}
              
            />
            {tags.length > 0 ? (
              <Chips label={P.tags}>
                {tags.map((tag) => <Chip key={tag} sm onRemove={() => setTags(tags.filter((t) => t !== tag))}>{tag}</Chip>)}
              </Chips>
            ) : null}
          </Stack>
        </Field>
        <Field label={P.priorRequisition}>
          <Select options={priorOptions} value={prior} onChange={setPrior}  />
        </Field>
        <Field label={P.lastContact}>
          <Input type="date" value={lastContact} onChange={(event) => setLastContact(event.target.value)}  />
        </Field>
        <Field label={P.notes}>
          <TextArea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)}  />
        </Field>
      </Stack>
    </Dialog>
  );
}

// ---- Merge -----------------------------------------------------------------------------------------------------------
function MergeDialog({ profile, candidateId, withToken, flags, onClose }: {
  profile: CandidateProfileOutput;
  candidateId: string;
  withToken: string | null;
  flags: URLSearchParams;
  onClose: () => void;
}) {
  if (!withToken) return <MergePick candidateId={candidateId} flags={flags} onClose={onClose} />;
  return <MergeForm profile={profile} candidateId={candidateId} withToken={withToken} onClose={onClose} />;
}

/** No second profile named yet: search the profiles this seat may see and choose one. */
function MergePick({ candidateId, flags, onClose }: { candidateId: string; flags: URLSearchParams; onClose: () => void }) {
  const route = useRoute();
  const [q, setQ] = useState("");
  const search = useSettledRead(["candidate-search", "merge", q], () => candidateSearch({ q: q.trim() || null, status: "Active", limit: 25 }), { enabled: q.trim().length >= 2 });
  const rows = (search.data?.rows ?? []).filter((row: SearchRow) => row.projection === "full" && row.candidate_id && row.candidate_id !== candidateId);
  const path = route.raw.split("?")[0] ?? "";
  return (
    <Dialog preset="merge" width={640} onClose={onClose} dismissLabel={ACTION_LABELS.cancel} >
      <Stack gap={12}>
        <FilterBar label={ACTION_LABELS.merge} search={{ value: q, onChange: setQ, hs: "cand.merge.search" }} />
        <Table dense label={ACTION_LABELS.merge} cols={2} head={<Tr><Th>{L.name}</Th><Th>{L.owner}</Th></Tr>}>
          {rows.map((row, index) => (
            <Tr key={row.ref}>
              <Td label={L.name}>
                <TextLink href={`#${withQuery(path, { with: row.candidate_id ?? "" }, flags)}`} >
                  {row.display_name ?? row.candidate_no}
                </TextLink>
              </Td>
              <Td label={L.owner}>{row.owner_name ?? "—"}</Td>
            </Tr>
          ))}
        </Table>
      </Stack>
    </Dialog>
  );
}

function MergeForm({ profile, candidateId, withToken, onClose }: {
  profile: CandidateProfileOutput;
  candidateId: string;
  withToken: string;
  onClose: () => void;
}) {
  const other = useSettledRead(["candidate-profile", withToken, null], () => candidateProfile({ candidate_id: withToken }));
  const [keepId, setKeepId] = useState(withToken);
  const [picks, setPicks] = useState<Record<string, number | undefined>>({});
  const [reason, setReason] = useState("");
  const outcome = useDialogOutcome();
  const merge = useWrite(
    (input: Parameters<typeof candidateMerge>[0], key) => candidateMerge(input, key),
    [["candidate-profile"], ["candidate-search"], ["candidate-timeline"], ["candidate-duplicate"]],
  );
  const second = other.data;
  if (other.error) {
    return (
      <Dialog preset="merge" onClose={onClose} dismissLabel={ACTION_LABELS.cancel} refused={{ reason: refusalSentence(other.error) ?? BRD.adminMerges }}  />
    );
  }
  const records = second ? [profile, second] : [profile];
  const kept = records.findIndex((record) => record.candidate_id === keepId);
  const keptIndex = kept < 0 ? 0 : kept;
  const attributes: CompareAttribute[] = MERGE_ROWS.map(({ key, label, value }) => ({
    key,
    label,
    values: records.map((record) => value(record) ?? null),
    hs: records.map((_, index) => `cand.merge.${key.replace(/_/g, "-")}.${index}`),
  }));
  const value = Object.fromEntries(MERGE_ROWS.map(({ key }) => [key, picks[key] ?? keptIndex]));
  const counts = (record: CandidateProfileOutput) => [(record.applications ?? []).length, (record.resume_versions ?? []).length] as const;
  const [apps, resumes] = records.reduce<[number, number]>((sum, record) => [sum[0] + counts(record)[0], sum[1] + counts(record)[1]], [0, 0]);
  const survivor = records[keptIndex];
  const merged = records[keptIndex === 0 ? 1 : 0];
  const ready = Boolean(second && survivor && merged && reason.trim().length >= 3);
  return (
    <Dialog
      preset="merge"
      width={840}
      onClose={onClose}
      dismissLabel={ACTION_LABELS.cancel}
      hint={SYNC_LABELS.recoveryAvailable}
      refused={outcome.refused ? { reason: outcome.refused.stale ? PROFILE_RULES.optimisticConcurrency : outcome.refused.reason } : undefined}
      primary={{
        label: ACTION_LABELS.merge,
        blocked: ready ? undefined : { reason: BRD.mergePreservesHistory },
        busy: merge.isPending,
        onClick: () => {
          if (!survivor?.candidate_id || !merged?.candidate_id) return;
          void outcome.run(() => merge.mutateAsync({
            candidate_id: survivor.candidate_id ?? "",
            merged_id: merged.candidate_id ?? "",
            expected_version: survivor.version,
            merged_expected_version: merged.version,
            reason: reason.trim(),
            keep: mergeKeep(value, keptIndex),
          })).then((out: CandidateMergeOutput | null) => { if (out) go(profileHref({ token: out.survivor.candidate_id ?? candidateId })); });
        },
        hs: "cand.merge.save",
      }}
      
    >
      <Stack gap={12}>
        {outcome.strip}
        {second ? (
          <>
            <RadioTile
              label={G6_LABELS.useExisting}
              row
              value={keepId}
              onChange={(id) => { setKeepId(id); setPicks({}); }}
              options={records.map((record, index) => ({
                value: record.candidate_id ?? "",
                label: <IdPill value={record.candidate_no} />,
                sub: record.display_name ?? undefined,
                hs: `cand.merge.keep.${index}`,
              }))}
            />
            <Compare
              label={ACTION_LABELS.merge}
              records={records.map((record) => ({ id: record.candidate_no, name: record.display_name ?? "" }))}
              attributes={attributes}
              preview={{ value, keep: keptIndex, onChange: (attribute, record) => setPicks((prev) => ({ ...prev, [attribute]: record })) }}
            />
            <KeyValue items={[[L.applications, apps], [DOCUMENT_LABELS.resumeVersions, resumes]]} cols={2} />
          </>
        ) : null}
        <Field label={G1_LABELS.reason} required>
          <TextArea rows={2} value={reason} onChange={(event) => setReason(event.target.value)}  />
        </Field>
      </Stack>
    </Dialog>
  );
}

// ---- Recover ---------------------------------------------------------------------------------------------------------
function RecoverDialog({ profile, candidateId, onClose, onDone }: {
  profile: CandidateProfileOutput;
  candidateId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const recovery = profile.recovery;
  const [reason, setReason] = useState("");
  const outcome = useDialogOutcome();
  const recover = useWrite(
    (input: CandidateUnmergeInput, key) => callTool<CandidateMergeOutput>("candidate.unmerge", input, { idempotencyKey: key }),
    [["candidate-profile"], ["candidate-search"], ["candidate-timeline"]],
  );
  if (!recovery) return null;
  return (
    <Dialog
      preset="recover"
      onClose={onClose}
      dismissLabel={ACTION_LABELS.cancel}
      refused={outcome.refused ? { reason: outcome.refused.stale ? PROFILE_RULES.optimisticConcurrency : outcome.refused.reason } : undefined}
      primary={{
        label: ACTION_LABELS.recover,
        blocked: reason.trim().length >= 3 ? undefined : { reason: PROFILE_RULES.mergeRecovery },
        busy: recover.isPending,
        onClick: () => {
          void outcome.run(() => recover.mutateAsync({
            candidate_id: candidateId,
            merge_event_id: recovery.merge_event_id,
            expected_version: recovery.survivor_version,
            merged_expected_version: recovery.merged_version,
            reason: reason.trim(),
          })).then((out) => { if (out) onDone(); });
        },
        hs: "cand.recover.save",
      }}
      
    >
      <Stack gap={12}>
        {outcome.strip}
        <Banner tone="info">{PROFILE_RULES.mergeRecovery}</Banner>
        <KeyValue items={[[PROFILE_STATUS.merged, <IdPill key="m" value={profile.candidate_no} />], [G6_LABELS.useExisting, <IdPill key="s" value={recovery.survivor_no} />]]} cols={2} />
        <Field label={G1_LABELS.reason} required>
          <TextArea rows={2} value={reason} onChange={(event) => setReason(event.target.value)}  />
        </Field>
      </Stack>
    </Dialog>
  );
}
